import {
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { data, Form, useActionData } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { ErrorList, Field, TextareaField } from '#app/components/forms.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { fadeInUp } from '#app/utils/animations.ts'
import { sendContactEmails } from '#app/utils/email.server.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/contact.ts'

const ContactFormSchema = z.object({
	name: z.string().min(1, 'Naam is verplicht').max(100, 'Naam is te lang'),
	email: z.string().email('Ongeldig emailadres').min(1, 'Email is verplicht'),
	subject: z
		.string()
		.min(1, 'Onderwerp is verplicht')
		.max(200, 'Onderwerp is te lang'),
	message: z
		.string()
		.min(10, 'Bericht moet minimaal 10 karakters bevatten')
		.max(1000, 'Bericht is te lang'),
})

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = parseWithZod(formData, {
		schema: ContactFormSchema,
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { name, email, subject, message } = submission.value

	await sendContactEmails({ contact: { name, email, subject, message } })

	return data({
		result: submission.reply({
			resetForm: true,
			fieldErrors: {},
		}),
		success: true as const,
	})
}

export async function loader() {
	return data({})
}

export const handle = { i18n: 'common' }

export default function ContactRoute() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const { t } = useTranslation()

	const [form, fields] = useForm({
		id: 'contact-form',
		constraint: getZodConstraint(ContactFormSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ContactFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	const isSuccess = Boolean(
		actionData && 'success' in actionData && actionData.success,
	)

	return (
		<div>
			{/* Header */}
			<motion.section
				className="pt-[120px] pb-16"
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.15 }}
				variants={fadeInUp}
			>
				<div className="container">
					<div className="eyebrow mb-4">{t('contact')}</div>
					<h1 className="display" style={{ fontSize: 'clamp(48px, 8vw, 112px)', maxWidth: '14ch' }}>
						{t('contactHeadingLine1')}<br /><span style={{ color: 'var(--muted-gallery)' }}>{t('contactHeadingLine2')}</span>
					</h1>
				</div>
			</motion.section>

			<motion.section
				className="pb-[120px] pt-10"
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.15 }}
				variants={fadeInUp}
			>
				<div className="container">
					<div className="grid gap-20 md:grid-cols-[1fr_1.2fr]">
						{/* Left — Contact info */}
						<div className="flex flex-col gap-8">
							<div>
								<div className="eyebrow mb-2">{t('contactEmailLabel')}</div>
								<a href="mailto:info@elseringnalda.nl" className="font-heading text-2xl italic" style={{ color: 'var(--ink)', textDecoration: 'none' }}>
									info@elseringnalda.nl
								</a>
							</div>
							<div className="mt-6 rounded-[14px] p-8" style={{ background: 'var(--paper-2)' }}>
								<div className="eyebrow mb-3">{t('contactWorkInPerson')}</div>
								<p className="mb-4 text-sm" style={{ color: 'var(--ink-soft)' }}>
									{t('contactStudioVisit')}
								</p>
								<a href="/exposities" className="btn-gallery inline-flex">{t('exhibitions')}</a>
							</div>
						</div>

						{/* Right — Contact form */}
						<div>
							{isSuccess ? (
								<div className="rounded-[14px] p-12 text-center" style={{ background: 'var(--paper-2)' }}>
									<div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full" style={{ background: 'var(--moss)', color: '#fff' }}>
										<Icon name="check" className="h-7 w-7" />
									</div>
									<h3 className="display mb-3" style={{ fontSize: 28 }}>{t('contactSent')}</h3>
									<p style={{ color: 'var(--ink-soft)' }}>
										{t('contactMessageReceived')}
									</p>
								</div>
							) : (
								<Form method="post" {...getFormProps(form)} className="flex flex-col">
									<HoneypotInputs />
									<div className="grid gap-4 sm:grid-cols-2">
										<Field
											labelProps={{ children: t('contactNameLabel') }}
											inputProps={{
												...getInputProps(fields.name, { type: 'text' }),
											}}
											errors={fields.name.errors}
										/>
										<Field
											labelProps={{ children: t('contactEmailLabel') }}
											inputProps={{
												...getInputProps(fields.email, { type: 'email' }),
											}}
											errors={fields.email.errors}
										/>
									</div>

									<Field
										labelProps={{ children: t('contactSubjectLabel') }}
										inputProps={{
											...getInputProps(fields.subject, { type: 'text' }),
										}}
										errors={fields.subject.errors}
									/>

									<TextareaField
										labelProps={{ children: t('contactMessageLabel') }}
										textareaProps={{
											...getTextareaProps(fields.message),
											rows: 6,
										}}
										errors={fields.message.errors}
									/>

									<ErrorList errors={form.errors} id={form.errorId} />

									<StatusButton
										type="submit"
										disabled={isPending}
										status={isPending ? 'pending' : 'idle'}
										className="btn-gallery mt-4 self-start"
									>
										{t('contactSubmitButton')} <Icon name="arrow-right" className="h-4 w-4" />
									</StatusButton>
								</Form>
							)}
						</div>
					</div>
				</div>
			</motion.section>
		</div>
	)
}

export const meta = () => {
	return buildMeta({
		title: 'Contact',
		description:
			'Neem contact op met beeldend kunstenaar Else Ringnalda. Bel of mail.',
		url: '/contact',
	})
}
